import { ButtonColorEnum, ButtonTypeEnum, ButtonVariantEnum, SnackBarVariants } from '@app/app.enum';
import { history } from '@app/app.routes';
import withSnackBar from '@app/components/core/message.component';
import CustomButton from '@app/components/shared/custom-button.component';
import CustomDialogBox from '@app/components/shared/dialog.component';
import Icon from '@app/components/shared/icon.component';
import { AddNewUSPSTierRoutedFromTierDropDown, MIDRegex, notApplicableValue, MIDLength6and9Regex } from '@app/constants';
import * as models from '@app/models';
import Injector from '@app/services';
import { IntlService } from '@app/services/intl.service';
import { SharedService } from '@app/services/shared.service';
import { SystemConfigService } from '@app/services/system-config.service';
import { USPSTierConfigService } from '@app/services/usps-tier-config.service';
import {
	Button,
	Checkbox,
	Chip,
	Dialog,
	DialogContent,
	DialogTitle,
	Divider,
	FormControlLabel,
	Grid,
	IconButton,
	InputAdornment,
	ListItem,
	ListItemSecondaryAction,
	ListItemText,
	Menu,
	MenuItem,
	TextField,
	Typography,
} from '@material-ui/core';
import Axios from 'axios';
import { Field, Form, Formik, FormikProps, FormikValues } from 'formik';
import PopupState, { bindMenu, bindTrigger } from 'material-ui-popup-state';
import React, { Component } from 'react';
import * as Yup from 'yup';
import classNames from 'classnames';

const sharedService: SharedService = Injector.get(SharedService);
const uspsTierConfigService: USPSTierConfigService = Injector.get(USPSTierConfigService);
const intlService: IntlService = Injector.get(IntlService);
const systemConfigService: SystemConfigService = Injector.get(SystemConfigService);

interface IUSPSTierConfigFormFactory {
	ePostagePermitNum: string;
	ePostageWebToolId: string;
	ePostageWebToolPassword: string;
	ePostageMID: string;
	ePostageReturnsMID: string;
	eVSPermitNum: string;
	eVSWebToolId: string;
	eVSWebToolPassword: string;
	eVSMID: string;
	eVSReturnsMID: string;
	eVSWebtoolMID: string;
	ePostageWebtoolMID: string;
	mailOwnerMID: string;
	id: string;
	masterMID: string;
	tier: string;
	default: boolean;
	isGlobal: boolean;
	description: string;
	numberOfAssociatedAccounts: number;
	consumerKey: string;
	consumerSecret: string;
}

const uspsTierConfiguration: IUSPSTierConfigFormFactory = {
	ePostageMID: notApplicableValue,
	ePostageReturnsMID: notApplicableValue,
	eVSMID: notApplicableValue,
	eVSReturnsMID: notApplicableValue,
	eVSPermitNum: notApplicableValue,
	eVSWebToolId: notApplicableValue,
	eVSWebToolPassword: notApplicableValue,
	ePostagePermitNum: notApplicableValue,
	ePostageWebToolId: notApplicableValue,
	ePostageWebToolPassword: notApplicableValue,
	eVSWebtoolMID: notApplicableValue,
	ePostageWebtoolMID: notApplicableValue,
	mailOwnerMID: notApplicableValue,
	consumerKey: notApplicableValue,
	consumerSecret: notApplicableValue,
	id: '',
	masterMID: '',
	tier: '',
	default: false,
	description: '',
	isGlobal: false,
	numberOfAssociatedAccounts: 0,
};

const uspsTierConfigurationSchema = Yup.object().shape<Partial<IUSPSTierConfigFormFactory>>({
	tier: Yup.string()
		.required(intlService.GetFormattedMessage('usps-tier-config.tier-required-validation'))
		.max(100, intlService.GetFormattedMessage('usps-tier-config.tier-length-validation')),
	description: Yup.string()
		.trim()
		.max(
			50,
			intlService.GetFormattedMessage('usps-tier-config.description-length-validation', {
				0: 50,
			})
		),
	masterMID: Yup.string()
		.required(intlService.GetFormattedMessage('usps-tier-config.masterMID-required-validation'))
		.matches(MIDRegex, intlService.GetFormattedMessage('app.profile.mid-length.regex-validation')),
	eVSWebToolId: Yup.string().max(
		100,
		intlService.GetFormattedMessage('usps-tier-config.eVSWebtoolId-length-validation')
	),
	eVSWebToolPassword: Yup.string().max(
		100,
		intlService.GetFormattedMessage('usps-tier-config.eVSWebtoolPassword-length-validation')
	),
	eVSPermitNum: Yup.string().max(
		100,
		intlService.GetFormattedMessage('usps-tier-config.eVSPermitNum-length-validation')
	),
	ePostageWebToolId: Yup.string().max(
		100,
		intlService.GetFormattedMessage('usps-tier-config.ePostageWebtoolId-length-validation')
	),
	ePostageWebToolPassword: Yup.string().max(
		100,
		intlService.GetFormattedMessage('usps-tier-config.ePostageWebtoolPassword-length-validation')
	),
	ePostageMID: Yup.string().matches(
		MIDLength6and9Regex,
		intlService.GetFormattedMessage('app.carrier-account.config.mid-length-validation')
	),
	ePostageReturnsMID: Yup.string().matches(
		MIDRegex,
		intlService.GetFormattedMessage('app.profile.mid-length.regex-validation')
	),
	eVSMID: Yup.string().matches(MIDLength6and9Regex, intlService.GetFormattedMessage('app.carrier-account.config.mid-length-validation')),
	eVSReturnsMID: Yup.string().matches(
		MIDRegex,
		intlService.GetFormattedMessage('app.profile.mid-length.regex-validation')
	),
	ePostagePermitNum: Yup.string().max(
		100,
		intlService.GetFormattedMessage('usps-tier-config.ePostagePermitNum-length-validation')
	),
	default: Yup.bool().notRequired().default(false),
	isGlobal: Yup.bool().notRequired().default(false),
	eVSWebtoolMID: Yup.string().matches(
		MIDLength6and9Regex,
		intlService.GetFormattedMessage('app.carrier-account.config.mid-length-validation')
	),
	ePostageWebtoolMID: Yup.string().matches(
		MIDLength6and9Regex,
		intlService.GetFormattedMessage('app.carrier-account.config.mid-length-validation')
	),
	mailOwnerMID: Yup.string().matches(
		MIDRegex,
		intlService.GetFormattedMessage('app.profile.mid-length.regex-validation')
	),
    consumerKey: Yup.string()
    .notRequired()
    .test(
        'is-empty-or-all-populated',
        intlService.GetFormattedMessage('app.carrier-account.config.consumer-key-secret-both-required'),
        function (value) {
            const { consumerSecret } = this.parent;
            const isConsumerSecretEmpty = !consumerSecret;
            const isCurrentFieldEmpty = !value;
            
            if(consumerSecret === notApplicableValue && (value === notApplicableValue || isCurrentFieldEmpty)){
                return true;
            }

            if(value === notApplicableValue && !isConsumerSecretEmpty){
                return false;
            }
            
            if (isCurrentFieldEmpty) {
                return isConsumerSecretEmpty;
            }

            return true;
        }
    ),
    consumerSecret: Yup.string()
    .notRequired()
    .test(
        'is-empty-or-all-populated',
        intlService.GetFormattedMessage('app.carrier-account.config.consumer-key-secret-both-required'),
        function (value) {
            const { consumerKey } = this.parent;
            const isConsumerKeyEmpty = !consumerKey;
            const isCurrentFieldEmpty = !value;
                
            if(consumerKey === notApplicableValue && (value === notApplicableValue || isCurrentFieldEmpty)){
                return true;
            }

            if(value === notApplicableValue && !isConsumerKeyEmpty){
                return false;
            }

            if (isCurrentFieldEmpty) {
                return isConsumerKeyEmpty;
            }

            return true;
        }
    ),
	numberOfAssociatedAccounts: Yup.number(),
});

class USPSTiersConfigComponent extends Component<models.IUSPSTierConfigProps, models.IUSPSTierConfigState> {
	private USPSTierConfigFactory = uspsTierConfiguration;
	private formRef = React.createRef<Formik<FormikValues>>();

	constructor(props: any) {
		super(props);

		this.state = {
			configs: [],
			openAddConfigDialog: false,
			openDeleteDialog: false,
			dialogTitle: '',
			editMode: false,
			configId: null,
			buttonDisabled: false,
			tierSearchValue: '',
			openUpdateDialog: false,
		};
	}

	public componentDidMount() {
		this.getUSPSTierConfigs();
		if (history.location.state?.extraParams?.isRoutedFrom === AddNewUSPSTierRoutedFromTierDropDown) {
			this.getCreditCardFeeAndMasterMID();
			this.manageDialog(true, intlService.GetFormattedMessage('usps-tier-config.dialog-add-label'), null);
		}
	}

	public async getCreditCardFeeAndMasterMID() {
		try {
			const systemConfig = await systemConfigService.getCreditCardFeeAndMasterMID();
			uspsTierConfiguration.masterMID = systemConfig.uspsMasterMID;
		} catch (error) {
			if (!Axios.isCancel(error)) {
				this.props.openSnackBar(sharedService.parseError(error.data), SnackBarVariants.Error);
			}
		}
	}

	public render() {
		return (
			<>
				{/* Delete config component */}
				<CustomDialogBox
					open={this.state.openDeleteDialog}
					dialogTitle={intlService.GetFormattedMessage('usps-tier-config.delete-config-label')}
					dialogDescription={intlService.GetFormattedMessage('usps-tier-config.config-delete-dialog-description')}
					closeCustomDialog={async (dialogPanel: string, isCancel: boolean) => {
						try {
							if (!isCancel) {
								await this.deleteUSPSTierConfig(this.state.configId);
								const configs = this.state.configs.filter((e: models.IUSPSTierConfig) => e.id !== this.state.configId);
								this.setState({
									configId: null,
									configs,
								});
								this.props.openSnackBar(
									intlService.GetFormattedMessage('app.snackbar.usps-tier-config-deleted-successfully')
								);
							}
							this.setState({
								openDeleteDialog: false,
							});
						} catch (error) {
							this.setState({
								openDeleteDialog: false,
							});
							this.props.openSnackBar(sharedService.parseError(error.data), SnackBarVariants.Error);
						}
					}}
				/>
				{/* Edit config component */}
				<CustomDialogBox
					open={this.state.openUpdateDialog}
					dialogTitle={intlService.GetFormattedMessage('usps-tier-config.dialog-edit-label')}
					dialogDescription={
						<>
							{intlService.GetFormattedMessage('usps-tier-config.config-edit-dialog-description')}{' '}
							{intlService.GetFormattedMessage('app.common.do-you-want-to-continue-text')}
						</>
					}
					closeCustomDialog={async (dialogPanel: string, isCancel: boolean) => {
						try {
							if (!isCancel) {
								// call edit function
								this.formRef.current.submitForm();
								this.setState({
									openUpdateDialog: false,
								});
							} else {
								this.setState({
									openUpdateDialog: false,
								});
							}
						} catch (error) {
							this.props.openSnackBar(sharedService.parseError(error.data), SnackBarVariants.Error);
						}
					}}
				/>
				<Grid container={true}>
					<Grid item={true} md={6} lg={4} xl={4}>
						<Button
							startIcon={<Icon name='plus' />}
							fullWidth={true}
							variant='text'
							onClick={() =>
								this.manageDialog(true, intlService.GetFormattedMessage('usps-tier-config.dialog-add-label'), null)
							}
						>
							<strong>{intlService.GetFormattedMessage('usps-tier-config.add-label')}</strong>
						</Button>
					</Grid>
					<Grid item={true} md={6} lg={8} xl={8}>
						<TextField
							fullWidth={true}
							variant='outlined'
							placeholder={intlService.GetFormattedMessage('usps-tier-config.search-tier-placeholder')}
							InputProps={{
								startAdornment: (
									<InputAdornment position='start'>
										<Icon name='magnifying-glass' />
									</InputAdornment>
								),
							}}
							value={this.state.tierSearchValue}
							onChange={(e) => {
								this.setState({
									tierSearchValue: e.target.value,
								});
							}}
							size='small'
						/>
					</Grid>
					<Grid container={true} className='pt-0 pb-0'>
						{this.uspsTierDialog()}
						{this.state.configs
							.filter(
								(e) =>
									!this.state.tierSearchValue || e.tier.toLowerCase().includes(this.state.tierSearchValue.toLowerCase())
							)
							.map((e: any, index: number) => (
								<Grid item={true} key={index} xs={12} sm={12} md={12} lg={12} xl={12}>
									{this.getTierConfigsList(e)}
									<Divider />
								</Grid>
							))}
					</Grid>
				</Grid>
			</>
		);
	}

	private getUSPSTierConfigs = async () => {
		try {
			const res = await uspsTierConfigService.getUSPSTierConfigs();
			this.setState({
				configs: SharedService.moveElementsToStart(res, (x) => !x.default),
			});
		} catch (error) {
			if (!Axios.isCancel(error)) {
				this.props.openSnackBar(sharedService.parseError(error.data), SnackBarVariants.Error);
			}
		}
	};

	private deleteUSPSTierConfig = async (id: string) => {
		try {
			await uspsTierConfigService.deleteUSPSTierConfigs(id);
		} catch (error) {
			if (!Axios.isCancel(error)) {
				throw error;
			}
		}
	};

	private manageDialog = (open: boolean, dialogTitle: string, data: models.IUSPSTierConfig) => {
		if (!open) {
			this.setState({
				buttonDisabled: false,
				editMode: false,
			});
		}

		if (data) {
			// replacing null or blank values of MID with 'NA'
			data.eVSMID = !data.eVSMID ? notApplicableValue : data.eVSMID;
			data.eVSWebtoolMID = !data.eVSWebtoolMID ? notApplicableValue : data.eVSWebtoolMID;
			data.eVSReturnsMID = !data.eVSReturnsMID ? notApplicableValue : data.eVSReturnsMID;
			data.ePostageMID = !data.ePostageMID ? notApplicableValue : data.ePostageMID;
			data.ePostageWebtoolMID = !data.ePostageWebtoolMID ? notApplicableValue : data.ePostageWebtoolMID;
			data.ePostageReturnsMID = !data.ePostageReturnsMID ? notApplicableValue : data.ePostageReturnsMID;
			data.eVSWebToolId = !data.eVSWebToolId ? notApplicableValue : data.eVSWebToolId;
			data.eVSWebToolPassword = !data.eVSWebToolPassword ? notApplicableValue : data.eVSWebToolPassword;
			data.eVSPermitNum = !data.eVSPermitNum ? notApplicableValue : data.eVSPermitNum;
			data.ePostageWebToolId = !data.ePostageWebToolId ? notApplicableValue : data.ePostageWebToolId;
			data.ePostageWebToolPassword = !data.ePostageWebToolPassword ? notApplicableValue : data.ePostageWebToolPassword;
			data.ePostagePermitNum = !data.ePostagePermitNum ? notApplicableValue : data.ePostagePermitNum;
			data.mailOwnerMID = !data.mailOwnerMID ? notApplicableValue : data.mailOwnerMID;
			data.consumerKey = !data.consumerKey ? notApplicableValue : data.consumerKey;
			data.consumerSecret = !data.consumerSecret ? notApplicableValue : data.consumerSecret;
		}

		this.USPSTierConfigFactory = !!data ? { ...data } : uspsTierConfiguration;
		uspsTierConfiguration.masterMID = this.props.uspsMasterMID ?? this.props.uspsMasterMID;
		this.setState({
			openAddConfigDialog: open,
			dialogTitle,
		});
	};

	private uspsTierDialog = () => (
		<Dialog
			open={this.state.openAddConfigDialog}
			fullWidth={true}
			maxWidth='md'
			onClose={() => this.manageDialog(false, null, null)}
		>
			<DialogTitle>{this.state.dialogTitle}</DialogTitle>
			<DialogContent>
				<Formik
					initialValues={this.USPSTierConfigFactory}
					ref={this.formRef}
					onSubmit={async (values: FormikValues) => {
						try {
							this.setState({
								buttonDisabled: true,
							});

							const data = values;

							let res = null;
							let snackBarMessage = null;
							if (this.state.editMode) {
								res = await uspsTierConfigService.updateUSPSTierConfigs(this.state.configId, data);
								const configs = this.state.configs;
								const index = configs.findIndex((e) => e.id === this.state.configId);
								configs[index] = res;
								this.setState({
									editMode: false,
									configId: null,
									configs: SharedService.moveElementsToStart(configs, (x) => !x.default),
								});
								snackBarMessage = intlService.GetFormattedMessage('app.snackbar.usps-tier-config-updated-successfully');
							} else {
								res = await uspsTierConfigService.createUSPSTierConfigs(data);
								const configs = this.state.configs;
								configs.unshift(res);
								this.setState({
									configs: SharedService.moveElementsToStart(configs, (x) => !x.default),
								});
								snackBarMessage = intlService.GetFormattedMessage('app.snackbar.usps-tier-config-added-successfully');
							}
							this.manageDialog(false, null, null);
							this.props.openSnackBar(snackBarMessage);
						} catch (err) {
							this.setState({
								buttonDisabled: false,
							});
							if (!Axios.isCancel(err)) {
								this.props.openSnackBar(sharedService.parseError(err.data), SnackBarVariants.Error);
							}
						}
					}}
					validationSchema={uspsTierConfigurationSchema}
					enableReinitialize={true}
				>
					{(formikProps: FormikProps<IUSPSTierConfigFormFactory>) => (
						<Form noValidate={true} className='width-100'>
							<Grid container={true} justifyContent='flex-start' alignItems='center' spacing={2}>
								<Grid item={true} sm={12} md={12} lg={12} xl={12}>
									<Field
										name='tier'
										label={intlService.GetFormattedMessage('usps-tier-config.tier-label')}
										variant='outlined'
										component={TextField}
										type='text'
										fullWidth={true}
										required={true}
										value={formikProps.values.tier}
										onChange={(e: React.ChangeEvent) => formikProps.handleChange('tier')(e)}
										error={!!((formikProps.values.tier.length || formikProps.submitCount) && !!formikProps.errors.tier)}
										helperText={
											formikProps.values.tier.length || formikProps.submitCount ? formikProps.errors.tier : null
										}
									/>
								</Grid>
								<Grid item={true} sm={12}>
									<Field
										name='description'
										variant='outlined'
										label={intlService.GetFormattedMessage('usps-tier-config.description-label')}
										component={TextField}
										type='text'
										fullWidth={true}
										value={formikProps.values.description}
										error={
											!!(
												(formikProps.values.description?.length || formikProps.submitCount) &&
												!!formikProps.errors.description
											)
										}
										helperText={
											formikProps.values.description?.length || formikProps.submitCount
												? formikProps.errors.description
												: null
										}
										onChange={(e: React.ChangeEvent) => formikProps.handleChange('description')(e)}
									/>
								</Grid>
								<Grid item={true} sm={6} md={6} lg={6} xl={6}>
									<Field
										name='masterMID'
										variant='outlined'
										label={intlService.GetFormattedMessage('usps-tier-config.master-mid-label')}
										component={TextField}
										type='text'
										fullWidth={true}
										required={true}
										value={formikProps.values.masterMID}
										onChange={(e: React.ChangeEvent) => formikProps.handleChange('masterMID')(e)}
										error={
											!!(
												(formikProps.values.masterMID.length || formikProps.submitCount) &&
												!!formikProps.errors.masterMID
											)
										}
										helperText={
											formikProps.values.masterMID.length || formikProps.submitCount
												? formikProps.errors.masterMID
												: null
										}
									/>
								</Grid>
								<Grid item={true} sm={6} md={6} lg={6} xl={6}>
									<Field
										name='mailOwnerMID'
										variant='outlined'
										label={intlService.GetFormattedMessage('usps-tier-config.mail-owner-mid-label')}
										component={TextField}
										type='text'
										fullWidth={true}
										value={formikProps.values.mailOwnerMID}
										onChange={(e: React.ChangeEvent) => formikProps.handleChange('mailOwnerMID')(e)}
										error={
											!!(
												(formikProps.values.mailOwnerMID.length || formikProps.submitCount) &&
												!!formikProps.errors.mailOwnerMID
											)
										}
										helperText={
											formikProps.values.mailOwnerMID.length || formikProps.submitCount
												? formikProps.errors.mailOwnerMID
												: null
										}
									/>
								</Grid>
								<Grid item={true} sm={6} md={6} lg={6} xl={6}>
									<Field
										name='eVSMID'
										variant='outlined'
										label={
											intlService.GetFormattedMessage('usps-tier-config.eVS-MID-label') +
											' ' +
											intlService.GetFormattedMessage('app.common-leave-blank-to-auto-populate-label')
										}
										component={TextField}
										type='text'
										fullWidth={true}
										value={formikProps.values.eVSMID}
										onChange={(e: React.ChangeEvent) => formikProps.handleChange('eVSMID')(e)}
										error={
											!!((formikProps.values.eVSMID.length || formikProps.submitCount) && !!formikProps.errors.eVSMID)
										}
										helperText={
											formikProps.values.eVSMID.length || formikProps.submitCount ? formikProps.errors.eVSMID : null
										}
									/>
								</Grid>
								<Grid item={true} sm={6} md={6} lg={6} xl={6}>
									<Field
										name='ePostageMID'
										variant='outlined'
										label={
											intlService.GetFormattedMessage('usps-tier-config.ePostage-MID-label') +
											' ' +
											intlService.GetFormattedMessage('app.common-leave-blank-to-auto-populate-label')
										}
										component={TextField}
										type='text'
										fullWidth={true}
										value={formikProps.values.ePostageMID}
										onChange={(e: React.ChangeEvent) => formikProps.handleChange('ePostageMID')(e)}
										error={
											!!(
												(formikProps.values.ePostageMID.length || formikProps.submitCount) &&
												!!formikProps.errors.ePostageMID
											)
										}
										helperText={
											formikProps.values.ePostageMID.length || formikProps.submitCount
												? formikProps.errors.ePostageMID
												: null
										}
									/>
								</Grid>
								<Grid item={true} sm={6} md={6} lg={6} xl={6}>
									<Field
										name='eVSReturnsMID'
										variant='outlined'
										label={
											intlService.GetFormattedMessage('usps-tier-config.eVS-Returns-MID-label') +
											' ' +
											intlService.GetFormattedMessage('app.common-leave-blank-to-auto-populate-label')
										}
										component={TextField}
										type='text'
										fullWidth={true}
										value={formikProps.values.eVSReturnsMID}
										onChange={(e: React.ChangeEvent) => formikProps.handleChange('eVSReturnsMID')(e)}
										error={
											!!(
												(formikProps.values.eVSReturnsMID.length || formikProps.submitCount) &&
												!!formikProps.errors.eVSReturnsMID
											)
										}
										helperText={
											formikProps.values.eVSReturnsMID.length || formikProps.submitCount
												? formikProps.errors.eVSReturnsMID
												: null
										}
									/>
								</Grid>
								<Grid item={true} sm={6} md={6} lg={6} xl={6}>
									<Field
										name='ePostageReturnsMID'
										variant='outlined'
										label={
											intlService.GetFormattedMessage('usps-tier-config.ePostage-Returns-MID-label') +
											' ' +
											intlService.GetFormattedMessage('app.common-leave-blank-to-auto-populate-label')
										}
										component={TextField}
										type='text'
										fullWidth={true}
										value={formikProps.values.ePostageReturnsMID}
										onChange={(e: React.ChangeEvent) => formikProps.handleChange('ePostageReturnsMID')(e)}
										error={
											!!(
												(formikProps.values.ePostageReturnsMID.length || formikProps.submitCount) &&
												!!formikProps.errors.ePostageReturnsMID
											)
										}
										helperText={
											formikProps.values.ePostageReturnsMID.length || formikProps.submitCount
												? formikProps.errors.ePostageReturnsMID
												: null
										}
									/>
								</Grid>
								<Grid item={true} sm={6} md={6} lg={6} xl={6}>
									<Field
										name='eVSWebtoolMID'
										variant='outlined'
										label={intlService.GetFormattedMessage('usps-tier-config.eVS-Webtool-MID-label')}
										component={TextField}
										type='text'
										fullWidth={true}
										value={formikProps.values.eVSWebtoolMID}
										onChange={(e: React.ChangeEvent) => formikProps.handleChange('eVSWebtoolMID')(e)}
										error={
											!!(
												(formikProps.values.eVSWebtoolMID.length || formikProps.submitCount) &&
												!!formikProps.errors.eVSWebtoolMID
											)
										}
										helperText={
											formikProps.values.eVSWebtoolMID.length || formikProps.submitCount
												? formikProps.errors.eVSWebtoolMID
												: null
										}
									/>
								</Grid>
								<Grid item={true} sm={6} md={6} lg={6} xl={6}>
									<Field
										name='ePostageWebtoolMID'
										variant='outlined'
										label={intlService.GetFormattedMessage('usps-tier-config.ePostage-Webtool-MID-label')}
										component={TextField}
										type='text'
										fullWidth={true}
										value={formikProps.values.ePostageWebtoolMID}
										onChange={(e: React.ChangeEvent) => formikProps.handleChange('ePostageWebtoolMID')(e)}
										error={
											!!(
												(formikProps.values.ePostageWebtoolMID.length || formikProps.submitCount) &&
												!!formikProps.errors.ePostageWebtoolMID
											)
										}
										helperText={
											formikProps.values.ePostageWebtoolMID.length || formikProps.submitCount
												? formikProps.errors.ePostageWebtoolMID
												: null
										}
									/>
								</Grid>
								<Grid item={true} sm={6} md={6} lg={6} xl={6}>
									<Field
										name='eVSWebToolId'
										variant='outlined'
										label={intlService.GetFormattedMessage('usps-tier-config.eVS-WebtoolId-label')}
										component={TextField}
										type='text'
										fullWidth={true}
										value={formikProps.values.eVSWebToolId}
										onChange={(e: React.ChangeEvent) => formikProps.handleChange('eVSWebToolId')(e)}
										error={
											!!(
												(formikProps.values.eVSWebToolId.length || formikProps.submitCount) &&
												!!formikProps.errors.eVSWebToolId
											)
										}
										helperText={
											formikProps.values.eVSWebToolId.length || formikProps.submitCount
												? formikProps.errors.eVSWebToolId
												: null
										}
									/>
								</Grid>
								<Grid item={true} sm={6} md={6} lg={6} xl={6}>
									<Field
										name='ePostageWebToolId'
										variant='outlined'
										label={intlService.GetFormattedMessage('usps-tier-config.ePostage-WebtoolId-label')}
										component={TextField}
										type='text'
										fullWidth={true}
										value={formikProps.values.ePostageWebToolId}
										onChange={(e: React.ChangeEvent) => formikProps.handleChange('ePostageWebToolId')(e)}
										error={
											!!(
												(formikProps.values.ePostageWebToolId?.length || formikProps.submitCount) &&
												!!formikProps.errors.ePostageWebToolId
											)
										}
										helperText={
											formikProps.values.ePostageWebToolId?.length || formikProps.submitCount
												? formikProps.errors.ePostageWebToolId
												: null
										}
									/>
								</Grid>
								<Grid item={true} sm={6} md={6} lg={6} xl={6}>
									<Field
										name='eVSWebToolPassword'
										variant='outlined'
										label={intlService.GetFormattedMessage('usps-tier-config.eVS-WebtoolPassword-label')}
										component={TextField}
										type='text'
										fullWidth={true}
										value={formikProps.values.eVSWebToolPassword}
										onChange={(e: React.ChangeEvent) => formikProps.handleChange('eVSWebToolPassword')(e)}
										error={
											!!(
												(formikProps.values.eVSWebToolPassword?.length || formikProps.submitCount) &&
												!!formikProps.errors.eVSWebToolPassword
											)
										}
										helperText={
											formikProps.values.eVSWebToolPassword?.length || formikProps.submitCount
												? formikProps.errors.eVSWebToolPassword
												: null
										}
									/>
								</Grid>
								<Grid item={true} sm={6} md={6} lg={6} xl={6}>
									<Field
										name='ePostageWebToolPassword'
										variant='outlined'
										label={intlService.GetFormattedMessage('usps-tier-config.ePostage-WebtoolPassword-label')}
										component={TextField}
										type='text'
										fullWidth={true}
										value={formikProps.values.ePostageWebToolPassword}
										onChange={(e: React.ChangeEvent) => formikProps.handleChange('ePostageWebToolPassword')(e)}
										error={
											!!(
												(formikProps.values.ePostageWebToolPassword?.length || formikProps.submitCount) &&
												!!formikProps.errors.ePostageWebToolPassword
											)
										}
										helperText={
											formikProps.values.ePostageWebToolPassword?.length || formikProps.submitCount
												? formikProps.errors.ePostageWebToolPassword
												: null
										}
									/>
								</Grid>
								<Grid item={true} sm={6} md={6} lg={6} xl={6}>
									<Field
										name='eVSPermitNum'
										variant='outlined'
										label={intlService.GetFormattedMessage('usps-tier-config.eVS-Permit-Number-label')}
										component={TextField}
										type='text'
										fullWidth={true}
										value={formikProps.values.eVSPermitNum}
										onChange={(e: React.ChangeEvent) => formikProps.handleChange('eVSPermitNum')(e)}
										error={
											!!(
												(formikProps.values.eVSPermitNum.length || formikProps.submitCount) &&
												!!formikProps.errors.eVSPermitNum
											)
										}
										helperText={
											formikProps.values.eVSPermitNum.length || formikProps.submitCount
												? formikProps.errors.eVSPermitNum
												: null
										}
									/>
								</Grid>
								<Grid item={true} sm={6} md={6} lg={6} xl={6}>
									<Field
										name='ePostagePermitNum'
										variant='outlined'
										label={intlService.GetFormattedMessage('usps-tier-config.ePostage-Permit-Number-label')}
										component={TextField}
										type='text'
										fullWidth={true}
										value={formikProps.values.ePostagePermitNum}
										onChange={(e: React.ChangeEvent) => formikProps.handleChange('ePostagePermitNum')(e)}
										error={
											!!(
												(formikProps.values.ePostagePermitNum.length || formikProps.submitCount) &&
												!!formikProps.errors.ePostagePermitNum
											)
										}
										helperText={
											formikProps.values.ePostagePermitNum.length || formikProps.submitCount
												? formikProps.errors.ePostagePermitNum
												: null
										}
									/>
								</Grid>
								<Grid item={true} xs={12} alignItems='center'>
									<Grid container={true} item={true} xs={12} className='mb-1' alignItems='center'>
										<Typography
											noWrap={true}
											color={'inherit'}
											variant='body1'
											className={classNames('display-inline-block')}
										>
											{intlService.GetFormattedMessage('usps-ship-app-credentials-label')}
										</Typography>
									</Grid>
									<Grid container={true} justifyContent='flex-start' alignItems='center' spacing={2}>
										<Grid item={true} sm={6} md={6} lg={6} xl={6}>
											<Field
												name='consumerKey'
												variant='outlined'
												label={intlService.GetFormattedMessage('usps-tier-config.consumer-key-label')}
												component={TextField}
												type='text'
												fullWidth={true}
												value={formikProps.values.consumerKey}
												onChange={(e: React.ChangeEvent) => formikProps.handleChange('consumerKey')(e)}
												error={
													!!(
														(formikProps.values.consumerKey.length || formikProps.submitCount) &&
														!!formikProps.errors.consumerKey
													)
												}
												helperText={
													formikProps.values.consumerKey.length || formikProps.submitCount
														? formikProps.errors.consumerKey
														: null
												}
											/>
										</Grid>
										<Grid item={true} sm={6} md={6} lg={6} xl={6}>
											<Field
												name='consumerSecret'
												variant='outlined'
												label={intlService.GetFormattedMessage('usps-tier-config.consumer-secret-label')}
												component={TextField}
												type='text'
												fullWidth={true}
												value={formikProps.values.consumerSecret}
												onChange={(e: React.ChangeEvent) => formikProps.handleChange('consumerSecret')(e)}
												error={
													!!(
														(formikProps.values.consumerSecret.length || formikProps.submitCount) &&
														!!formikProps.errors.consumerSecret
													)
												}
												helperText={
													formikProps.values.consumerSecret.length || formikProps.submitCount
														? formikProps.errors.consumerSecret
														: null
												}
											/>
										</Grid>
									</Grid>
								</Grid>

								<Grid item={true} sm={6} md={6} lg={6} xl={6}>
									<FormControlLabel
										control={
											<Checkbox
												checked={formikProps.values.default}
												onChange={(e) => {
													formikProps.handleChange('default')(e);
													if (e.target?.checked) {
														formikProps.setFieldValue('isGlobal', true);
													}
												}}
											/>
										}
										label={intlService.GetFormattedMessage('app.snackbar.usps-tier-config-default-label')}
									/>
								</Grid>
								<Grid item={true} sm={6} md={6} lg={6} xl={6}>
									<FormControlLabel
										control={
											<Checkbox
												checked={formikProps.values.isGlobal}
												disabled={formikProps.values.default}
												onChange={(e) => formikProps.handleChange('isGlobal')(e)}
											/>
										}
										label={intlService.GetFormattedMessage('app.usps-tier-config-global-label')}
									/>
								</Grid>
							</Grid>
						</Form>
					)}
				</Formik>
				<Grid container={true} justifyContent='flex-end' alignItems='center' spacing={2}>
					<Grid item={true}>
						<CustomButton
							variant={ButtonVariantEnum.Outlined}
							color={ButtonColorEnum.Primary}
							buttonText='Cancel'
							type={ButtonTypeEnum.Reset}
							onClick={() => this.manageDialog(false, null, null)}
						/>
					</Grid>
					<Grid item={true}>
						<CustomButton
							buttonText='Save'
							type={ButtonTypeEnum.Button}
							onClick={() => {
								if (this.state.editMode) {
									this.setState({
										openUpdateDialog: true,
									});
								} else {
									this.formRef.current.submitForm();
								}
							}}
							loading={this.state.buttonDisabled}
							disabled={this.state.buttonDisabled}
						/>
					</Grid>
				</Grid>
			</DialogContent>
		</Dialog>
	);

	private renderList = (config: models.IUSPSTierConfig) => {
		return (
			<Grid container={true}>
				<Grid item={true}>
					<PopupState variant='popper'>
						{(popupState: models.IMenuInjectedProps) => (
							<React.Fragment>
								<IconButton {...bindTrigger(popupState)} color='inherit'>
									<Icon name='ellipsis-vertical' className='icon-dimension-square' />
								</IconButton>
								<Menu {...bindMenu(popupState)}>
									<MenuItem
										onClick={() => {
											this.setState({
												editMode: true,
												configId: config.id,
											});
											this.manageDialog(
												true,
												intlService.GetFormattedMessage('usps-tier-config.dialog-edit-label'),
												config
											);
											popupState.close();
										}}
									>
										{intlService.GetFormattedMessage('app.app-view.app-card.edit')}
									</MenuItem>
									<MenuItem
										onClick={() => {
											this.setState({
												openDeleteDialog: true,
												configId: config.id,
											});
											popupState.close();
										}}
									>
										{intlService.GetFormattedMessage('app.user.delete')}
									</MenuItem>
								</Menu>
							</React.Fragment>
						)}
					</PopupState>
				</Grid>
			</Grid>
		);
	};

	private getTierConfigsList = (config: models.IUSPSTierConfig) => (
		<React.Fragment key={config.id}>
			<ListItem ContainerComponent='div'>
				<ListItemText>
					<Grid container={true} spacing={1}>
						<Grid item={true} xs={12}>
							<Grid container={true}>
								<Grid item={true} xs={3}>
									<Typography variant='subtitle2' color='inherit'>
										<strong>{`${intlService.GetFormattedMessage('usps-tier-config.tier-label')}`}</strong>
									</Typography>
								</Grid>
								<Grid item={true} xs={9}>
									<Grid container={true} spacing={1} alignItems='center'>
										<Grid item={true}>
											<Typography variant='subtitle2' color='inherit'>
												<strong>{config.tier}</strong>
											</Typography>
										</Grid>
										{config.default && (
											<Grid item={true}>
												<strong>
													<Chip
														label={intlService.GetFormattedMessage(`app.usps-tier-config-default-label`)}
														color='default'
														size='small'
													/>
												</strong>
											</Grid>
										)}
										{config.isGlobal && (
											<Grid item={true}>
												<strong>
													<Chip
														label={intlService.GetFormattedMessage(`app.usps-tier-config-global-label`)}
														color='default'
														size='small'
													/>
												</strong>
											</Grid>
										)}
									</Grid>
								</Grid>
							</Grid>
						</Grid>
						<Grid item={true} xs={12}>
							<Grid container={true}>
								<Grid item={true} xs={3}>
									<Typography variant='subtitle2' color='inherit' noWrap={true}>
										<strong>{`${intlService.GetFormattedMessage('usps-tier-config.description-label')}`}</strong>
									</Typography>
								</Grid>
								<Grid item={true} xs={3}>
									<Typography variant='body2' color='inherit' noWrap={true}>
										{config.description || notApplicableValue}
									</Typography>
								</Grid>
								<Grid item={true} xs={3}>
									<Typography variant='subtitle2' color='inherit' noWrap={true}>
										<strong>{`${intlService.GetFormattedMessage('usps-tier-config.master-mid-label')}`}</strong>
									</Typography>
								</Grid>
								<Grid item={true} xs={3}>
									<Typography variant='body2' color='inherit' noWrap={true}>
										{config.masterMID}
									</Typography>
								</Grid>
								<Grid item={true} xs={3}>
									<Typography variant='subtitle2' color='inherit' noWrap={true}>
										<strong>{`${intlService.GetFormattedMessage('usps-tier-config.mail-owner-mid-label')}`}</strong>
									</Typography>
								</Grid>
								<Grid item={true} xs={3}>
									<Typography variant='body2' color='inherit' noWrap={true}>
										{config.mailOwnerMID || notApplicableValue}
									</Typography>
								</Grid>
								<Grid item={true} xs={3}>
									<Typography variant='subtitle2' color='inherit' noWrap={true}>
										<strong>{`${intlService.GetFormattedMessage('usps-tier-config.eVS-MID-label')}`}</strong>
									</Typography>
								</Grid>
								<Grid item={true} xs={3}>
									<Typography variant='body2' color='inherit' noWrap={true}>
										{config.eVSMID || notApplicableValue}
									</Typography>
								</Grid>
								<Grid item={true} xs={3}>
									<Typography variant='subtitle2' color='inherit' noWrap={true}>
										<strong>{`${intlService.GetFormattedMessage('usps-tier-config.ePostage-MID-label')}`}</strong>
									</Typography>
								</Grid>
								<Grid item={true} xs={3}>
									<Typography variant='body2' color='inherit' noWrap={true}>
										{config.ePostageMID || notApplicableValue}
									</Typography>
								</Grid>
								<Grid item={true} xs={3}>
									<Typography variant='subtitle2' color='inherit' noWrap={true}>
										<strong>{`${intlService.GetFormattedMessage('usps-tier-config.eVS-Returns-MID-label')}`}</strong>
									</Typography>
								</Grid>
								<Grid item={true} xs={3}>
									<Typography variant='body2' color='inherit' noWrap={true}>
										{config.eVSReturnsMID || notApplicableValue}
									</Typography>
								</Grid>
								<Grid item={true} xs={3}>
									<Typography variant='subtitle2' color='inherit' noWrap={true}>
										<strong>
											{`${intlService.GetFormattedMessage('usps-tier-config.ePostage-Returns-MID-label')}`}
										</strong>
									</Typography>
								</Grid>
								<Grid item={true} xs={3}>
									<Typography variant='body2' color='inherit' noWrap={true}>
										{config.ePostageReturnsMID || notApplicableValue}
									</Typography>
								</Grid>
								<Grid item={true} xs={3}>
									<Typography variant='subtitle2' color='inherit' noWrap={true}>
										<strong>{`${intlService.GetFormattedMessage('usps-tier-config.eVS-Webtool-MID-label')}`}</strong>
									</Typography>
								</Grid>
								<Grid item={true} xs={3}>
									<Typography variant='body2' color='inherit' noWrap={true}>
										{config.eVSWebtoolMID || notApplicableValue}
									</Typography>
								</Grid>
								<Grid item={true} xs={3}>
									<Typography variant='subtitle2' color='inherit' noWrap={true}>
										<strong>{`${intlService.GetFormattedMessage(
											'usps-tier-config.ePostage-Webtool-MID-label'
										)}`}</strong>
									</Typography>
								</Grid>
								<Grid item={true} xs={3}>
									<Typography variant='body2' color='inherit' noWrap={true}>
										{config.ePostageWebtoolMID || notApplicableValue}
									</Typography>
								</Grid>
								<Grid item={true} xs={3}>
									<Typography variant='subtitle2' color='inherit' noWrap={true}>
										<strong>{`${intlService.GetFormattedMessage('usps-tier-config.eVS-WebtoolId-label')}`}</strong>
									</Typography>
								</Grid>
								<Grid item={true} xs={3}>
									<Typography variant='body2' color='inherit' noWrap={true}>
										{config.eVSWebToolId || notApplicableValue}
									</Typography>
								</Grid>
								<Grid item={true} xs={3}>
									<Typography variant='subtitle2' color='inherit' noWrap={true}>
										<strong>{`${intlService.GetFormattedMessage('usps-tier-config.ePostage-WebtoolId-label')}`}</strong>
									</Typography>
								</Grid>
								<Grid item={true} xs={3}>
									<Typography variant='body2' color='inherit' noWrap={true}>
										{config.ePostageWebToolId || notApplicableValue}
									</Typography>
								</Grid>
								<Grid item={true} xs={3}>
									<Typography variant='subtitle2' color='inherit' noWrap={true}>
										<strong>{`${intlService.GetFormattedMessage(
											'usps-tier-config.eVS-WebtoolPassword-label'
										)}`}</strong>
									</Typography>
								</Grid>
								<Grid item={true} xs={3}>
									<Typography variant='body2' color='inherit' noWrap={true}>
										{config.eVSWebToolPassword || notApplicableValue}
									</Typography>
								</Grid>
								<Grid item={true} xs={3}>
									<Typography variant='subtitle2' color='inherit' noWrap={true}>
										<strong>{`${intlService.GetFormattedMessage(
											'usps-tier-config.ePostage-WebtoolPassword-label'
										)}`}</strong>
									</Typography>
								</Grid>
								<Grid item={true} xs={3}>
									<Typography variant='body2' color='inherit' noWrap={true}>
										{config.ePostageWebToolPassword || notApplicableValue}
									</Typography>
								</Grid>
								<Grid item={true} xs={3}>
									<Typography variant='subtitle2' color='inherit' noWrap={true}>
										<strong>{`${intlService.GetFormattedMessage('usps-tier-config.eVS-Permit-Number-label')}`}</strong>
									</Typography>
								</Grid>
								<Grid item={true} xs={3}>
									<Typography variant='body2' color='inherit' noWrap={true}>
										{config.eVSPermitNum || notApplicableValue}
									</Typography>
								</Grid>

								<Grid item={true} xs={3}>
									<Typography variant='subtitle2' color='inherit' noWrap={true}>
										<strong>{`${intlService.GetFormattedMessage(
											'usps-tier-config.ePostage-Permit-Number-label'
										)}`}</strong>
									</Typography>
								</Grid>
								<Grid item={true} xs={3}>
									<Typography variant='body2' color='inherit' noWrap={true}>
										{config.ePostagePermitNum || notApplicableValue}
									</Typography>
								</Grid>
								<Grid item={true} xs={3}>
									<Typography variant='subtitle2' color='inherit' noWrap={true}>
										<strong>{`${intlService.GetFormattedMessage('usps-tier-config.consumer-key-label')}`}</strong>
									</Typography>
								</Grid>
								<Grid item={true} xs={3}>
									<Typography variant='body2' color='inherit' noWrap={true}>
										{config.consumerKey || notApplicableValue}
									</Typography>
								</Grid>
								<Grid item={true} xs={3}>
									<Typography variant='subtitle2' color='inherit' noWrap={true}>
										<strong>{`${intlService.GetFormattedMessage('usps-tier-config.consumer-secret-label')}`}</strong>
									</Typography>
								</Grid>
								<Grid item={true} xs={3}>
									<Typography variant='body2' color='inherit' noWrap={true}>
										{config.consumerSecret || notApplicableValue}
									</Typography>
								</Grid>
								<Grid item={true} xs={3}>
									<Typography variant='subtitle2' color='inherit' noWrap={true}>
										<strong>{`${intlService.GetFormattedMessage(
											'usps-tier-config.number-of-associated-accounts-label'
										)}`}</strong>
									</Typography>
								</Grid>
								<Grid item={true} xs={3}>
									<Typography variant='body2' color='inherit' noWrap={true}>
										{config.numberOfAssociatedAccounts}
									</Typography>
								</Grid>
							</Grid>
						</Grid>
					</Grid>
				</ListItemText>
				<ListItemSecondaryAction>{this.renderList(config)}</ListItemSecondaryAction>
			</ListItem>
		</React.Fragment>
	);
}

export default withSnackBar(USPSTiersConfigComponent);



// WEBPACK FOOTER //
// ./src/components/admin/setting/usps-tiers.component.tsx